import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';
import { Select as ChakraSelect, type SingleValue } from 'chakra-react-select';
import { type FormEvent, useState } from 'react';

import { FilterId } from '../../../filters/types/filters';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import type { PlotParameter } from '../../PageConfigProvider/types/item';
import {
  defaultReportParameterFilters,
  filterCanBePrimary,
  toReadableFilterName,
} from './parameters';

export function EditParameterModal({
  isOpen,
  onClose,
  mode,
  initial,
  onSubmit,
  isChartLevel = false,
  plotParameter,
  onGroupingChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  initial: ReportParameterConfig;
  onSubmit: (param: ReportParameterConfig) => void;
  isChartLevel?: boolean;
  plotParameter?: PlotParameter;
  onGroupingChange?: (grouped: boolean) => void;
}) {
  const [draft, setDraft] = useState<ReportParameterConfig>({
    ...initial,
    selectionLimit: initial.selectionLimit ?? 1,
  });

  const [groupingDraft, setGroupingDraft] = useState<boolean>(
    plotParameter?.grouped ?? true
  );

  const availableFilterIds = defaultReportParameterFilters;

  type FilterOption = {
    value: FilterId;
    label: string;
    isDisabled?: boolean;
  };

  const save = () => {
    onSubmit(draft);
    if (isChartLevel && onGroupingChange) {
      onGroupingChange(groupingDraft);
    }
    onClose();
  };

  const showGroupingUI =
    isChartLevel && !initial.isPrimary && (draft.selectionLimit ?? 1) > 1;

  const unitOfAnalysisOptions = availableFilterIds.filter(filterCanBePrimary);

  const options: FilterOption[] = unitOfAnalysisOptions.map((id) => ({
    value: id,
    label: id === FilterId.COMPANY ? 'Company' : toReadableFilterName(id),
  }));

  const selected: FilterOption | null =
    options.find((opt) => opt.value === draft.filterId) ?? null;

  const handleSelectChange = (opt: SingleValue<FilterOption>) => {
    if (!opt) return;
    const nextFilterId = opt.value;
    setDraft((prev) => {
      const prevDefault = toReadableFilterName(prev.filterId);
      const nextDefault =
        nextFilterId === FilterId.COMPANY
          ? 'Company'
          : toReadableFilterName(nextFilterId);
      const shouldUpdateName = !prev.name || prev.name === prevDefault;
      return {
        ...prev,
        filterId: nextFilterId,
        name: shouldUpdateName ? nextDefault : prev.name,
      };
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent
        as="form"
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          save();
        }}
      >
        <ModalHeader color="text.primary">
          {mode === 'add' ? 'Add Parameter' : 'Edit Parameter'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={3}>
            <FormLabel>Parameters</FormLabel>
            <div data-testid="parameter-select">
              <ChakraSelect<FilterOption, false>
                options={options}
                value={selected}
                onChange={handleSelectChange}
                isSearchable
              />
            </div>
          </FormControl>
          <FormControl mb={3}>
            <FormLabel>Name</FormLabel>
            <Input
              value={draft.name}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  name: e.target.value,
                })
              }
            />
          </FormControl>
          <FormControl mb={3}>
            <FormLabel>Selection Limit</FormLabel>
            <NumberInput
              value={draft.selectionLimit ?? 1}
              min={1}
              onChange={(_, valueAsNumber) =>
                setDraft({
                  ...draft,
                  // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
                  selectionLimit: isNaN(valueAsNumber) ? 1 : valueAsNumber,
                })
              }
            >
              <NumberInputField />
            </NumberInput>
          </FormControl>
          {showGroupingUI && (
            <FormControl mb={3}>
              <Checkbox
                isChecked={groupingDraft}
                onChange={(e) => setGroupingDraft(e.target.checked)}
              >
                Group Parameter
              </Checkbox>
            </FormControl>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            size="sm"
            colorScheme="navyBlue"
            variant="ghost"
            mr={2}
            type="button"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button size="sm" colorScheme="green" type="submit">
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
